// Week 10 / Day 2 — Bank Light: Account, Card and Safety (bilingual RU/EN inline).
// Quest-style, playful review of Week 2 + part of Week 5. Universal ages 7–15; Max uses the middle level.
// Educational only — no real money, no account opening, no personal data.

export const maxWeek10Day2 = {
  id: 'max-week-10-day-2',
  studentId: 'max',
  weekId: 'week-10',
  dayId: 'day-2',
  title: 'Огонёк банка: счёт, карта и безопасность / Bank Light: Account, Card and Safety',
  shortTitle: 'Огонёк банка / Bank Light',
  theme:
    'Нимни вспоминает: банк помогает хранить записи, а безопасность защищает счёт. / Nimny remembers: a bank helps keep records, and safety protects the account.',
  estimatedTimeLabel:
    'Квестовая часть: около 20–30 минут. Паспорт миссии и короткая проверка с родителем: около 35–50 минут. / Quest part: about 20–30 minutes. Mission passport and short parent check: about 35–50 minutes.',
  nimnyIntro:
    'Второй огонёк спрятан у маленького банка Нимни. Чтобы его зажечь, нужно вспомнить счёт, баланс, карту, пароль, PIN и безопасные решения. / The second light is hidden near Nimny\'s little bank. To light it, remember account, balance, card, password, PIN and safe choices.',
  goals: [
    'Огонёк знаний № 2: банк, счёт, карта и безопасность. / Light of knowledge #2: bank, account, card and safety.',
    'Слой 1. Математика / Layer 1. Math: лёгкие задачки на баланс и комиссию (Grade 5 / middle).',
    'Слой 2. Русский язык / Layer 2. Russian: словарь огонька банка и мини-диктант.',
    'Слой 3. Чтение и короткое письмо / Layer 3. Reading and short writing: 3–5 предложений «Какие правила безопасности ты запомнил?». / "What safety rules do you remember?".',
    'Слой 4. Карточки безопасности / Layer 4. Safety cards: безопасно / опасно / спросить взрослого. / safe / dangerous / ask an adult.',
    'Слой 5. Паспорт миссии / Layer 5. Mission passport: подбор банковских терминов и рисунок огонька. / Match bank terms and draw the light.',
    'Дневник мышления / Thinking Diary.',
    'Награда нимами / Reward in nims: 3/5.'
  ],
  vocabulary: [
    { term: 'банк / bank', definition: 'место, которое хранит записи о нимах / a place that keeps records about nims' },
    { term: 'счёт / account', definition: 'твоя страница в банке / your page in the bank' },
    { term: 'баланс / balance', definition: 'сколько нимов на счёте / how many nims are on the account' },
    { term: 'карта / card', definition: 'ключ к счёту / a key to the account' },
    { term: 'пароль / password', definition: 'тайное слово / a secret word' },
    { term: 'PIN / PIN', definition: 'тайный код карты / the card\'s secret code' },
    { term: 'комиссия / fee', definition: 'дополнительная плата / an extra cost' },
    { term: 'безопасность / safety', definition: 'правила, которые защищают / rules that protect' },
    { term: 'проверить / to check', definition: 'найти подтверждение / to find confirmation' },
    { term: 'взрослый / adult', definition: 'тот, кто помогает решить / one who helps decide' }
  ],
  onlineTasks: [
    { id: 'q1', type: 'math', title: 'Какой стал баланс? / What is the balance now?',
      prompt: 'Баланс счёта 80 нимов. Положили 20 нимов и потратили 15 нимов. Какой стал баланс? / Balance is 80 nims. Add 20, spend 15. What is the balance?',
      helper: '80 + 20 − 15. / 80 + 20 − 15.', answer: '85', unit: 'нимов / nims' },
    { id: 'q2', type: 'math', title: 'Сколько нимов не хватает? / How many nims are missing?',
      prompt: 'У Нимни 30 нимов, а нужно 35. Сколько нимов не хватает? / Nimny has 30 nims and needs 35. How many are missing?',
      helper: '35 − 30. / 35 − 30.', answer: '5', unit: 'нимов / nims' },
    { id: 'q3', type: 'math', title: 'Комиссия за 4 месяца / Fee for 4 months',
      prompt: 'Месячная комиссия — 3 нима. Сколько нимов за 4 месяца? / Monthly fee is 3 nims. How much for 4 months?',
      helper: '3 × 4. / 3 × 4.', answer: '12', unit: 'нимов / nims' },
    { id: 'q4', type: 'math', title: 'Привычка копить / Save habit',
      prompt: 'Нимни откладывает 10 нимов каждую неделю 3 недели подряд. Сколько отложила? / 10 nims a week for 3 weeks. How many in total?',
      helper: '10 × 3. / 10 × 3.', answer: '30', unit: 'нимов / nims' },
    { id: 'q5', type: 'classify', title: 'Карточки безопасности / Safety cards',
      prompt: 'Распредели карточки по 3 домикам. / Sort the cards into 3 houses.',
      categories: [
        { id: 'safe', label: 'Безопасно / Safe' },
        { id: 'danger', label: 'Опасно / Dangerous' },
        { id: 'ask', label: 'Спросить взрослого / Ask an adult' }
      ],
      items: [
        { id: 'cover_pin', text: 'прикрыть PIN рукой / cover the PIN with a hand' },
        { id: 'tell_friend', text: 'сказать пароль другу / tell a password to a friend' },
        { id: 'weird_msg', text: 'получить странное сообщение / receive a strange message' },
        { id: 'ask_parent', text: 'спросить родителя / ask the parent' },
        { id: 'unknown_site', text: 'ввести карту на незнакомом сайте / enter the card on an unknown website' },
        { id: 'check_source', text: 'проверить источник / check the source' },
        { id: 'read_terms', text: 'читать условия счёта с родителем / read account terms with a parent' },
        { id: 'open_alone', text: 'открыть счёт самому / open an account by myself' }
      ] },
    { id: 'q6', type: 'classify', title: 'Подбери определение / Match the term',
      prompt: 'Распредели определения. / Sort the definitions.',
      categories: [
        { id: 'checking', label: 'Текущий счёт / Checking account' },
        { id: 'savings',  label: 'Накопительный счёт / Savings account' },
        { id: 'fee',      label: 'Комиссия / Fee' },
        { id: 'overdraft', label: 'Перерасход / Overdraft' },
        { id: 'official', label: 'Официальный источник / Official source' }
      ],
      items: [
        { id: 'd_checking', text: 'счёт для повседневных покупок / for everyday spending' },
        { id: 'd_savings',  text: 'счёт, чтобы копить / for saving' },
        { id: 'd_fee',      text: 'дополнительная плата банку / an extra cost' },
        { id: 'd_overdraft', text: 'попытка потратить больше, чем есть / trying to spend more than you have' },
        { id: 'd_official', text: 'надёжная информация, проверяемая со взрослым / reliable info, parent-supported' }
      ] },
    { id: 'q7', type: 'text', title: 'Исправь ошибки / Fix the mistakes',
      prompt: 'Перепиши без ошибок и расставь знаки: «пароль и пин нелзя сообщать другим если сообщение кажеться странным спрасы взрослого». / Rewrite without mistakes (Russian sentence above).',
      placeholder: 'Пароль и PIN нельзя сообщать другим людям. / Don\'t tell password or PIN to others.' },
    { id: 'q8', type: 'essay', title: 'Какие правила безопасности ты запомнил? / Which safety rules do you remember?',
      prompt: 'Напиши 3–5 предложений по-русски: какие правила безопасности ты запомнил? Используй слова: пароль, PIN, источник, проверить, взрослый. / Write 3–5 Russian sentences using the keywords.',
      minSentences: 3, maxSentences: 5, placeholder: 'Я запомнил, что... / I remember that...' },
    { id: 'q9', type: 'essay', title: 'Тихий вопрос Нимни / A quiet question from Nimny',
      prompt: 'Напиши 2–4 предложения: что было самым важным в этой миссии? Используй слово «огонёк». / Write 2–4 sentences about the most important thing; use the word "огонёк".',
      minSentences: 2, maxSentences: 4, placeholder: 'Самое важное во втором огоньке знаний — это... / The most important in the second light of knowledge is...' }
  ],
  notebookInstructions: [
    'Заголовок: «Неделя 10, День 2 — Огонёк банка». / Heading: "Week 10, Day 2 — Bank Light".',
    'Слой 1. Математика / Layer 1. Math: q1–q4 + опц. сравнение комиссий A (2/мес × 6 = 12) и B (5 один раз). / + optional fee comparison.',
    'Слой 2. Русский язык / Layer 2. Russian: словарь и мини-диктант: «Пароль и PIN нельзя сообщать другим людям. Если сообщение кажется странным, спроси взрослого.» + правка. / vocabulary, mini-dictation, edit.',
    'Слой 3. Чтение и короткое письмо / Layer 3. Reading and short writing: 3–5 предложений «Какие правила безопасности ты запомнил?». / 3–5 sentences.',
    'Слой 4. Карточки безопасности / Layer 4. Safety cards: 8 карточек по 3 домикам. / 8 cards into 3 houses.',
    'Слой 5. Паспорт миссии / Layer 5. Mission passport: подбор 5 банковских терминов и рисунок второго огонька. / 5 bank terms matched and the second light drawing.',
    'Дневник мышления / Thinking diary.',
    'Награда нимами после проверки. / Reward in nims after parent check.'
  ],
  printWorksheet: {
    title: 'Неделя 10, День 2 / Week 10, Day 2',
    subtitle: 'Огонёк банка: счёт, карта и безопасность / Bank Light: Account, Card and Safety',
    pages: [
      { title: 'Маршрут миссии / Mission route', blocks: [
        { type: 'text', text: 'Поставь штамп ✦, когда выполнено. / Stamp ✦ when done.' },
        { type: 'list', items: [
          '✦ 1. Быстрая математика / Quick math.',
          '✦ 2. Русский язык — слова и мини-диктант / Russian — words and mini-dictation.',
          '✦ 3. Короткое письмо «Какие правила безопасности ты запомнил?» / Short writing.',
          '✦ 4. Карточки безопасности / Safety cards.',
          '✦ 5. Подбор банковских терминов и рисунок огонька / Bank terms and light drawing.',
          '✦ 6. Дневник мышления / Thinking diary.',
          '✦ 7. Проверка с родителем / Parent check.',
          '✦ 8. Печать миссии и нимы / Mission stamp and nims.'
        ] }
      ] },
      { title: 'Слой 1. Математика — быстрая миссия / Layer 1. Math — Quick mission', blocks: [
        { type: 'list', items: [
          '1) 80 + 20 − 15 = ____ нимов баланс / nims balance.',
          '2) 35 − 30 = ____ нимов не хватает / nims missing.',
          '3) 3 × 4 = ____ нимов комиссия за 4 месяца / fee for 4 months.',
          '4) 10 × 3 = ____ нимов отложено за 3 недели / saved in 3 weeks.',
          '5) Optional / Доп.: счёт A — 2 нима/мес × 6 = ____ ; счёт B — 5 нимов один раз. Какой счёт дороже за 6 месяцев? / Which account is more expensive over 6 months?',
          '6) Optional / Доп.: 30 + 5 = ____ нимов нужно собрать / nims to collect.'
        ] }
      ] },
      { title: 'Слой 2. Русский язык — слова и мини-диктант / Layer 2. Russian — Words and Mini-Dictation', blocks: [
        { type: 'text', text: 'Лёгкое повторение: словарь огонька банка. / Light review: bank-light vocabulary.' },
        { type: 'list', items: [
          'Словарь / Vocabulary: банк, счёт, баланс, карта, пароль, PIN, комиссия, безопасность, проверить, взрослый.',
          'Мини-диктант / Mini-dictation: «Пароль и PIN нельзя сообщать другим людям. Если сообщение кажется странным, спроси взрослого.»',
          'Исправь ошибки / Fix mistakes: «пароль и пин нелзя сообщать другим если сообщение кажеться странным спрасы взрослого».',
          'Орфограмма дня / Spelling rule of the day: безударные гласные и пунктуация. / Unstressed vowels and punctuation.'
        ] }
      ] },
      { title: 'Слой 3. Чтение и короткое письмо / Layer 3. Reading and Short Writing', blocks: [
        { type: 'list', items: [
          'Прочитай ещё раз слова огонька банка. / Re-read the bank-light vocabulary.',
          'Напиши 3–5 предложений «Какие правила безопасности ты запомнил?» / Write 3–5 Russian sentences "What safety rules do you remember?": ____________________________________________',
          'Используй слова: пароль, PIN, источник, проверить, взрослый / Use the keywords.'
        ] }
      ] },
      { title: 'Слой 4. Карточки безопасности и сортировка / Layer 4. Safety Cards and Sorting', blocks: [
        { type: 'text', text: 'Вырежи или отметь карточки и положи в 3 домика. / Cut or mark cards and place into 3 houses.' },
        { type: 'list', items: [
          '«прикрыть PIN рукой» → ▢ Безопасно ▢ Опасно ▢ Спросить взрослого',
          '«сказать пароль другу» → ▢ Безопасно ▢ Опасно ▢ Спросить взрослого',
          '«получить странное сообщение» → ▢ Безопасно ▢ Опасно ▢ Спросить взрослого',
          '«спросить родителя» → ▢ Безопасно ▢ Опасно ▢ Спросить взрослого',
          '«ввести карту на незнакомом сайте» → ▢ Безопасно ▢ Опасно ▢ Спросить взрослого',
          '«проверить источник» → ▢ Безопасно ▢ Опасно ▢ Спросить взрослого',
          '«читать условия счёта с родителем» → ▢ Безопасно ▢ Опасно ▢ Спросить взрослого',
          '«открыть счёт самому» → ▢ Безопасно ▢ Опасно ▢ Спросить взрослого'
        ] }
      ] },
      { title: 'Слой 5. Огонёк знаний — банковские термины и рисунок / Layer 5. Knowledge Light — Bank terms and Drawing (practical)', blocks: [
        { type: 'text', text: 'Подбери 5 банковских терминов и нарисуй второй огонёк знаний. / Match 5 bank terms and draw the second light.' },
        { type: 'list', items: [
          'Текущий счёт / Checking → ____',
          'Накопительный счёт / Savings → ____',
          'Комиссия / Fee → ____',
          'Перерасход / Overdraft → ____',
          'Официальный источник / Official source → ____',
          'Рисунок огонька банка / Bank-light drawing: ▢▢▢',
          'Подпись / Caption: «Я умею защищать счёт.»'
        ] }
      ] },
      { title: 'Дневник мышления / Thinking Diary', blocks: [
        { type: 'list', items: [
          'Самое важное правило безопасности сегодня / Most important safety rule today: ___________________',
          'Что я скажу маленькому ребёнку про безопасность? / What would I tell a younger child about safety? ___________________'
        ] }
      ] },
      { title: 'Родительская проверка, печать миссии и награда нимами / Parent check, mission stamp and reward in nims', blocks: [
        { type: 'list', items: [
          'Печать миссии / Mission stamp: ▢ ✦ ОГОНЁК БАНКА ЗАЖЖЁН ✦ / BANK LIGHT IS LIT.',
          'Базовая награда / Base reward: 3 нима / nims ✓',
          'Бонус за безопасные решения / Bonus for safe choices: +1 ☐',
          'Бонус за ясное объяснение правил банка / Bonus for a clear bank-rule explanation: +1 ☐',
          'Итого / Total: ____ из / of 5 нимов / nims'
        ] }
      ] }
    ]
  },
  parentGuide: {
    goal:
      'Лёгкая квестовая миссия: повторить идеи Недели 2 и часть Недели 5 (банк, счёт, баланс, карта, пароль, PIN, комиссия, перерасход, официальный источник, безопасность). Самостоятельная работа; роль родителя — короткое введение (3–5 минут), помощь только если ребёнок застрял, и финальная проверка (5–10 минут). Не экзамен. / Light quest mission reviewing Week 2 + part of Week 5. Child-led; parent gives 3–5 min intro, helps only if stuck, 5–10 min final check. Not a test.',
    safetyNote: 'Только учебная симуляция; никаких реальных счетов, личных данных, ввода карты на сайтах, регистрации в банках, переписки с незнакомцами. / Educational simulation only; no real accounts, no personal data, no card on websites, no bank registration, no contacting strangers.',
    mathGoal: 'q1: 80+20−15 = 85. q2: 35−30 = 5. q3: 3×4 = 12. q4: 10×3 = 30. Опц.: счёт A 2×6 = 12 нимов > счёт B 5 нимов; A дороже за 6 месяцев. 30+5 = 35. / Listed.',
    russianGoal: 'Словарь огонька банка запомнен; мини-диктант чистый; правка убирает все ошибки и ставит знаки. / Vocabulary recalled; clean dictation; mistakes fixed.',
    readingWritingGoal: '3–5 предложений по-русски с минимум 3 ключевыми словами. / 3–5 Russian sentences using ≥ 3 keywords.',
    missionGoal: '8 карточек безопасности распределены по 3 домикам без явных ошибок; 5 банковских терминов подобраны корректно. / 8 safety cards sorted; 5 bank terms matched correctly.',
    practicalGoal: 'Рисунок второго огонька подписан «Я умею защищать счёт». Никаких реальных счетов, ввода карты или регистрации. / Second-light drawing signed; no real accounts.',
    check: [
      'Q1: 85 нимов / 85 nims.',
      'Q2: 5 нимов / 5 nims.',
      'Q3: 12 нимов / 12 nims.',
      'Q4: 30 нимов / 30 nims.',
      'Карточки безопасности разложены / Safety cards sorted.',
      '5 банковских терминов подобраны / 5 bank terms matched.',
      '3–5 предложений «Какие правила безопасности ты запомнил?» / 3–5 sentences.',
      'Дневник мышления заполнен / Thinking diary filled.'
    ],
    answerKey: [
      { taskId: 'q1', answer: '80 + 20 − 15 = 85 нимов. / 85 nims.' },
      { taskId: 'q2', answer: '35 − 30 = 5 нимов не хватает. / 5 nims missing.' },
      { taskId: 'q3', answer: '3 × 4 = 12 нимов комиссии за 4 месяца. / 12 nims for 4 months.' },
      { taskId: 'q4', answer: '10 × 3 = 30 нимов отложено за 3 недели. / 30 nims saved.' },
      { taskId: 'q5', answer: 'Безопасно: прикрыть PIN рукой, спросить родителя, проверить источник, читать условия счёта с родителем. Опасно: сказать пароль другу, ввести карту на незнакомом сайте, открыть счёт самому. Спросить взрослого: получить странное сообщение. / Listed.' },
      { taskId: 'q6', answer: 'Текущий счёт — счёт для повседневных покупок. Накопительный — счёт, чтобы копить. Комиссия — дополнительная плата банку. Перерасход — попытка потратить больше, чем есть. Официальный источник — надёжная информация, проверяемая со взрослым.' },
      { taskId: 'q7', answer: 'Пароль и PIN нельзя сообщать другим людям. Если сообщение кажется странным, спроси взрослого.' },
      { taskId: 'q8', answer: 'Открытый ответ. 3–5 предложений по-русски с минимум 3 ключевыми словами и идеей: пароль и PIN — тайна, странные сообщения проверяй со взрослым, источник проверяй. / Open answer.' },
      { taskId: 'q9', answer: 'Открытый ответ. 2–4 предложения; слово «огонёк» использовано. / Open answer.' }
    ],
    spellingCheck: 'Словарь и диктант чистые; правка корректна. / Vocabulary recalled; clean dictation; edit correct.',
    readingCheck: '3–5 предложений по-русски; ключевые слова использованы. / 3–5 Russian sentences with keywords.',
    missionCheck: '8 карточек безопасности и 5 банковских терминов распределены без явных ошибок. / 8 safety cards and 5 bank terms sorted without clear errors.',
    notebookCheck: 'Дневник мышления заполнен; рисунок огонька подписан. / Diary filled; light drawing signed.',
    printCheck: 'Все 8 страниц паспорта миссии заполнены. / All 8 mission-passport pages completed.',
    discussionPrompts: [
      'Что должно оставаться в тайне? / What should stay secret?',
      'Что ты делаешь со странным сообщением? / What do you do with a strange message?',
      'Что происходит, если попытаться потратить больше, чем есть? / What happens if you try to spend more than you have?',
      'Зачем мы проверяем источники со взрослым? / Why do we check sources with an adult?'
    ],
    parentRoleNote: 'Введение 3–5 минут, помощь только если застрял, финальная проверка 5–10 минут и начисление нимов. Не экзамен. / 3–5 min intro, help only if stuck, 5–10 min final check, then award nims. Not a test.',
    ifEasy: 'Усложните: пусть ребёнок придумает 2 свои карточки в каждый домик и обоснует. / Make harder: 2 own cards per house with justification.',
    ifDifficult: 'Сократите: оставьте q1, q5 (4 карточки) и рисунок огонька. / Make easier: keep q1, q5 (4 cards) and the light drawing.',
    tokenReward:
      'После проверки можно начислить бонусные нимы через Parent Room. Нимы — это призовые токены курса, а не оплата. / After checking the work, the parent may add bonus nims through Parent Room. Nims are course prize tokens, not payment.'
  },
  reward: {
    unit: 'nim', unitName: 'ним', base: 3, max: 5,
    label: 'Награда за миссию / Mission reward', display: '3 нима / 3 nims',
    bonuses: [
      { id: 'safety-choice', label: 'Бонус за безопасные решения / Bonus for safe choices', amount: 1 },
      { id: 'clear-bank-explanation', label: 'Бонус за ясное объяснение правил банка / Bonus for a clear bank-rule explanation', amount: 1 }
    ]
  },
  completionRequirements: { requiredTaskIds: ['q1', 'q5', 'q6', 'q8', 'q9'], allowNotebookCompletion: true },
  nextLessonId: 'max-week-10-day-3'
};

export default maxWeek10Day2;
